import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import Input from '../ui/Input';
import StepIndicator from '../ui/StepIndicator';
import ConfirmModal from '../ui/ConfirmModal';
import ProductoAplicacionForm from './ProductoAplicacionForm';
import { obtenerCatalogoProductosAplicacion } from '../../services/productosAplicacion';
import { normalizarDecimal } from '../../utils/normalizarDecimal';

const TOTAL_STEPS = 4;
const STEP_LABELS = ['Ubicación', 'Datos de la aplicación', 'Productos', 'Resumen'];

const PRODUCTO_VACIO = { nombrecomercial: '', dosis: '' };

function hoyISO() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function buildInitialState(initialData) {
  return {
    huerto: initialData?.huerto ?? '',
    cuartel: initialData?.cuartel ?? '',
    fechaaplicacion: initialData?.fechaaplicacion ?? hoyISO(),
    horainicio: initialData?.horainicio ?? '',
    horafin: initialData?.horafin ?? '',
    ha: initialData?.ha ?? '',
    productos:
      initialData?.productos && initialData.productos.length > 0
        ? initialData.productos.map((p) => ({ nombrecomercial: p.nombrecomercial ?? '', dosis: p.dosis ?? '' }))
        : [{ ...PRODUCTO_VACIO }],
  };
}

// Wizard de 4 pasos reutilizado por Nueva aplicación y Editar aplicación.
// mode='create' guarda vía crearAplicacion; mode='edit' vía
// actualizarAplicacion — ambos casos delegan la escritura real en el
// `onSubmit` que recibe cada página, este componente solo maneja el UX.
//
// Productor: un encargado puede tener 1..N productores asignados
// (public.encargado_productor). En modo create, si tiene 2+, debe
// seleccionar uno (obligatorio, campo visible al inicio de "Ubicación").
// Si tiene exactamente 1 (el caso de casi todos los usuarios hoy), NO se
// muestra absolutamente nada de productor — ni selector ni texto
// informativo: la pantalla debe verse idéntica a como se veía antes de
// esta funcionalidad, y el único idproductor se usa y envía
// internamente sin que el usuario lo note. En modo edit el productor
// NUNCA se puede cambiar (decisión de esta fase): no hay selector ni
// campo informativo tampoco, y no se envía idproductor al guardar.
//
// HUERTO sigue siendo texto libre, sin catálogo ni validación — no cambia
// con la selección de productor.
function AplicacionForm({
  mode,
  initialData,
  productorNombre,
  productores = [],
  productoresStatus = 'ready',
  onSubmit,
  cancelTo,
}) {
  const navigate = useNavigate();
  const isEdit = mode === 'edit';
  const requiereSelectorProductor = !isEdit && productores.length > 1;

  const [initial] = useState(() => buildInitialState(initialData));
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');

  const [idproductor, setIdproductor] = useState(() => {
    if (!isEdit && productores.length === 1) return productores[0].id;
    return '';
  });

  // productores llega de forma ASÍNCRONA desde la página (fetch a
  // Supabase / cache offline): en el primer render normalmente todavía
  // está vacío, así que el useState de arriba no alcanza a autoasignar
  // nada. Este efecto reacciona cuando la lista termina de cargar y trae
  // exactamente 1 productor.
  useEffect(() => {
    if (!isEdit && productores.length === 1) {
      setIdproductor(productores[0].id);
    }
  }, [isEdit, productores]);

  const [huerto, setHuerto] = useState(initial.huerto);
  const [cuartel, setCuartel] = useState(initial.cuartel);
  const [fechaaplicacion, setFechaaplicacion] = useState(initial.fechaaplicacion);
  const [horainicio, setHorainicio] = useState(initial.horainicio);
  const [horafin, setHorafin] = useState(initial.horafin);
  const [ha, setHa] = useState(initial.ha);
  const [productos, setProductos] = useState(initial.productos);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showSalirConfirm, setShowSalirConfirm] = useState(false);

  const [catalogoProductos, setCatalogoProductos] = useState([]);
  const [catalogoStatus, setCatalogoStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setCatalogoStatus('loading');
      const result = await obtenerCatalogoProductosAplicacion();
      if (cancelled) return;

      if (!result.ok) {
        setCatalogoStatus('error');
        return;
      }
      setCatalogoProductos(result.productos);
      setCatalogoStatus(result.productos.length === 0 ? 'empty' : 'ready');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const nombreProductorActual = requiereSelectorProductor
    ? productores.find((p) => String(p.id) === String(idproductor))?.nombre || ''
    : productorNombre;

  const currentSnapshot = () =>
    JSON.stringify({ idproductor, huerto, cuartel, fechaaplicacion, horainicio, horafin, ha, productos });
  const initialSnapshot = JSON.stringify({ idproductor: isEdit ? '' : idproductor, ...initial });
  const hasChanges = () => currentSnapshot() !== initialSnapshot;

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      // Mientras el catálogo de productores todavía está cargando, no se
      // valida nada relacionado a productor: ni el selector (0 aparente)
      // ni el error de "sin productores" deben bloquear al usuario.
      if (!isEdit && productoresStatus === 'ready' && productores.length === 0) {
        return 'No tienes productores asignados. Contacta al administrador.';
      }
      if (requiereSelectorProductor && !idproductor) return 'Selecciona el productor.';
      if (!huerto.trim()) return 'Ingresa el huerto.';
      if (!cuartel.trim()) return 'Ingresa el cuartel.';
    }
    if (currentStep === 2) {
      if (!fechaaplicacion) return 'Ingresa la fecha de aplicación.';
      if (horainicio && horafin && horafin < horainicio) {
        return 'La hora de término debe ser igual o posterior a la hora de inicio.';
      }
      if (ha !== '' && (normalizarDecimal(ha) === null || normalizarDecimal(ha) <= 0)) {
        return 'La superficie (ha) debe ser mayor a 0.';
      }
    }
    if (currentStep === 3) {
      if (productos.length === 0) return 'Agrega al menos un producto.';
      if (productos.some((p) => !p.nombrecomercial.trim())) return 'Selecciona un producto.';
    }
    return '';
  };

  const handleContinuar = () => {
    const message = validateStep(step);
    if (message) {
      setStepError(message);
      return;
    }
    setStepError('');
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleAtras = () => {
    setStepError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSalir = () => {
    if (hasChanges()) {
      setShowSalirConfirm(true);
    } else {
      navigate(cancelTo);
    }
  };

  const handleAgregarProducto = () => {
    setProductos((prev) => [...prev, { ...PRODUCTO_VACIO }]);
  };

  const handleCambiarProducto = (index, producto) => {
    setProductos((prev) => prev.map((p, i) => (i === index ? producto : p)));
  };

  const handleEliminarProducto = (index) => {
    setProductos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuardar = async () => {
    setSaving(true);
    setSaveError('');

    const payload = {
      huerto: huerto.trim(),
      cuartel: cuartel.trim(),
      fechaaplicacion,
      horainicio: horainicio || null,
      horafin: horafin || null,
      ha: normalizarDecimal(ha),
      productos: productos.map((p) => ({
        nombrecomercial: p.nombrecomercial.trim(),
        dosis: p.dosis.trim(),
      })),
    };

    // El productor solo se envía al crear: en edición nunca se puede
    // cambiar (ver comentario de cabecera), así que el backend sigue
    // usando el idproductor ya guardado en la aplicación.
    //
    // Defensa: no depender solo del estado `idproductor` para el caso de
    // 1 productor — si React todavía no alcanzó a correr el efecto que lo
    // autoasigna, se resuelve aquí mismo a partir de `productores` para
    // no enviar idproductor=null al backend.
    if (!isEdit) {
      const idProductorEfectivo = productores.length === 1 ? productores[0].id : idproductor;
      payload.idproductor = idProductorEfectivo ? Number(idProductorEfectivo) : null;
    }

    const result = await onSubmit(payload);

    setSaving(false);

    if (!result.ok) {
      setSaveError(result.message);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <StepIndicator step={step} totalSteps={TOTAL_STEPS} label={STEP_LABELS[step - 1]} />

      {step === 1 && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Ubicación</h2>

          {/* Con 1 solo productor asignado (el caso de casi todos los
              usuarios hoy) esta pantalla no debe mostrar NADA de
              productor: ni selector ni texto informativo — debe verse
              idéntica a como se veía antes de esta funcionalidad. El
              productor se resuelve y envía internamente sin que el
              usuario lo note. */}
          {requiereSelectorProductor && (
            <div className="w-full">
              <label htmlFor="idproductor" className="mb-1 block text-sm font-medium text-gray-700">
                Productor *
              </label>
              <select
                id="idproductor"
                value={idproductor}
                onChange={(event) => setIdproductor(event.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-gray-300 px-4 text-base text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                <option value="">Seleccionar productor</option>
                {productores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="huerto"
              label="Huerto"
              value={huerto}
              onChange={(event) => setHuerto(event.target.value)}
              placeholder="Ej: Huerto Norte"
            />
            <Input
              id="cuartel"
              label="Cuartel"
              value={cuartel}
              onChange={(event) => setCuartel(event.target.value)}
              placeholder="Ej: C-04"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Datos de la aplicación</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              id="fechaaplicacion"
              label="Fecha"
              type="date"
              value={fechaaplicacion}
              onChange={(event) => setFechaaplicacion(event.target.value)}
            />
            <Input
              id="horainicio"
              label="Hora inicio (opcional)"
              type="time"
              value={horainicio}
              onChange={(event) => setHorainicio(event.target.value)}
            />
            <Input
              id="horafin"
              label="Hora término (opcional)"
              type="time"
              value={horafin}
              onChange={(event) => setHorafin(event.target.value)}
            />
            <Input
              id="ha"
              label="Superficie, ha (opcional)"
              type="text"
              inputMode="decimal"
              value={ha}
              onChange={(event) => setHa(event.target.value)}
              placeholder="Ej: 2,5"
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Productos</h2>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {productos.map((producto, index) => (
              <ProductoAplicacionForm
                key={index}
                index={index}
                producto={producto}
                onChange={(nuevo) => handleCambiarProducto(index, nuevo)}
                onRemove={() => handleEliminarProducto(index)}
                canRemove={productos.length > 1}
                catalogoProductos={catalogoProductos}
                catalogoStatus={catalogoStatus}
              />
            ))}
          </div>

          <Button variant="secondary" className="sm:w-auto sm:self-start sm:px-6" onClick={handleAgregarProducto}>
            + Agregar otro producto
          </Button>
        </div>
      )}

      {step === 4 && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Confirmar cambios' : 'Resumen aplicación'}
          </h2>

          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="font-medium text-gray-500">Productor</dt>
              <dd className="mt-1 text-gray-900">{nombreProductorActual}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Huerto</dt>
              <dd className="mt-1 text-gray-900">{huerto}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Cuartel</dt>
              <dd className="mt-1 text-gray-900">{cuartel}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Fecha</dt>
              <dd className="mt-1 text-gray-900">{fechaaplicacion}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora inicio</dt>
              <dd className="mt-1 text-gray-900">{horainicio || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Hora término</dt>
              <dd className="mt-1 text-gray-900">{horafin || '—'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Superficie</dt>
              <dd className="mt-1 text-gray-900">{ha !== '' ? `${ha} ha` : '—'}</dd>
            </div>
          </dl>

          <div>
            <p className="text-sm font-medium text-gray-500">Productos</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {productos.map((producto, index) => (
                <div key={index} className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">
                    {index + 1}. {producto.nombrecomercial}
                  </p>
                  {producto.dosis && <p className="text-xs text-gray-600">Dosis: {producto.dosis}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stepError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{stepError}</p>}
      {saveError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>}

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        {step === 1 ? (
          <Button variant="secondary" onClick={handleSalir} disabled={saving}>
            Cancelar
          </Button>
        ) : (
          <Button variant="secondary" onClick={handleAtras} disabled={saving}>
            Atrás
          </Button>
        )}

        {step < TOTAL_STEPS ? (
          <Button onClick={handleContinuar}>Continuar</Button>
        ) : (
          <Button onClick={handleGuardar} disabled={saving}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Confirmar y guardar'}
          </Button>
        )}
      </div>

      <ConfirmModal
        open={showSalirConfirm}
        title="¿Salir sin guardar?"
        confirmLabel="Salir"
        cancelLabel="Seguir editando"
        onConfirm={() => navigate(cancelTo)}
        onCancel={() => setShowSalirConfirm(false)}
      >
        <p>Vas a perder los cambios ingresados en esta aplicación.</p>
      </ConfirmModal>
    </div>
  );
}

export default AplicacionForm;
